import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
// Only the main client is used on this page.
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  RECOVERY_MESSAGES,
  REQUEST_NEW_LINK_PATH,
  RESET_SUCCESS_MESSAGE,
  completePasswordReset,
  parseRecoveryLink,
  resolveRecoverySession,
  validateNewPassword,
  type RecoveryState,
} from "@/lib/passwordRecovery";

type PageState = { status: "checking" } | RecoveryState;

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [state, setState] = useState<PageState>({ status: "checking" });
  // The link is read once: a recovery token can only be used one time (StrictMode runs effects twice).
  const resolution = useRef<Promise<RecoveryState> | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    let active = true;
    if (!resolution.current) {
      const link = parseRecoveryLink(window.location.href);
      resolution.current = resolveRecoverySession(supabase.auth, link).finally(() => {
        // Remove one-time tokens / errors from the address bar.
        if (link.kind !== "none") {
          window.history.replaceState(window.history.state, "", window.location.pathname);
        }
      });
    }
    resolution.current.then((result) => {
      if (active) setState(result);
    });
    return () => {
      active = false;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateNewPassword(password, confirmPassword);
    if (validationError) {
      toast({ title: "Erreur", description: validationError, variant: "destructive" });
      return;
    }

    setIsLoading(true);
    const result = await completePasswordReset(supabase.auth, password);
    setIsLoading(false);

    if ("message" in result) {
      toast({ title: "Erreur", description: result.message, variant: "destructive" });
      return;
    }

    toast(RESET_SUCCESS_MESSAGE);
    navigate(result.redirectTo, { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="w-full max-w-none bg-white shadow-xl">
          {state.status === "checking" && (
            <div className="p-10 flex flex-col items-center gap-4 text-center" role="status">
              <Loader2 className="h-8 w-8 animate-spin text-[#ea384c]" aria-hidden="true" />
              <p className="text-gray-700 font-medium">Vérification du lien…</p>
            </div>
          )}

          {state.status === "invalid" && (
            <>
              <CardHeader className="text-center space-y-2">
                <CardTitle className="text-2xl font-bold">Lien invalide ou expiré</CardTitle>
                <CardDescription className="text-gray-600">{RECOVERY_MESSAGES[state.reason]}</CardDescription>
              </CardHeader>
              <div className="p-6 pt-0 space-y-4">
                <Button asChild className="w-full bg-[#ea384c] hover:bg-[#ea384c]/90 text-white">
                  <Link to={REQUEST_NEW_LINK_PATH}>Demander un nouveau lien</Link>
                </Button>
                <div className="text-center">
                  <Link to="/connexion" className="text-sm text-gray-600 hover:underline">
                    Retour à la connexion
                  </Link>
                </div>
              </div>
            </>
          )}

          {state.status === "ready" && (
            <>
              <CardHeader className="text-center space-y-2">
                <CardTitle className="text-2xl font-bold">Nouveau mot de passe</CardTitle>
                <CardDescription className="text-gray-600">
                  Choisissez un nouveau mot de passe sécurisé
                </CardDescription>
              </CardHeader>

              <div className="p-6 space-y-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">Nouveau mot de passe</Label>
                    <Input
                      id="password"
                      type="password"
                      autoComplete="new-password"
                      placeholder="Minimum 8 caractères"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      placeholder="Confirmez votre mot de passe"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={8}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-[#ea384c] hover:bg-[#ea384c]/90 text-white"
                  >
                    {isLoading ? "Mise à jour..." : "Mettre à jour le mot de passe"}
                  </Button>
                </form>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
